import { useEffect, useState } from 'react';
import {
  FaCreditCard,
  FaBed,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaUtensils,
  FaBuilding,
  FaTag,
} from 'react-icons/fa';
import { MdRoomService } from 'react-icons/md';
import { TbHotelService } from 'react-icons/tb';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const Payment = ({ currentStep, setCurrentStep, totalSteps }) => {
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    roomCharges: false,
    amenities: false,
    securityDeposit: false,
    otherCharges: false,
    food: false,
    roomServices: false,
    checkoutAdjustments: false,
  });
  const [guestData, setGuestData] = useState(null);
  const [isCorporateGuest, setIsCorporateGuest] = useState(false);
  const [amountDue, setAmountDue] = useState(0);

  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isCorporateGuest = profileData.companyid && profileData.companyname;
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        return {
          first_name: profileData.firstname || '',
          last_name: profileData.lastname || '',
          email: profileData.emailid || '',
          phone_number: profileData.phonenumber || '',
          country_code: '+91',
          client_type: profileData.clienttype || 'Leisure',
          company_id: profileData.companyid || '',
          company_name: profileData.companyname || '',
          is_corporate: isCorporateGuest,
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      return {
        first_name: profileData.firstname || '',
        last_name: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
        phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
        country_code: '+91',
        client_type: profileData.clienttype || 'Leisure',
        company_id: profileData.companyid || '',
        company_name: profileData.companyname || '',
        is_corporate: isCorporateGuest,
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  useEffect(() => {
    const initializePayment = async () => {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      setBookingDetails(bookingDetails);

      const guestInfo = await getGuestData();
      setGuestData(guestInfo);
      setIsCorporateGuest(guestInfo?.is_corporate || false);

      const loadRazorpayScript = () => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => setRazorpayLoaded(true);
        script.onerror = () => {
          toast.error('Error loading Razorpay script. Please try again.');
        };
        document.body.appendChild(script);
      };

      if (!window.Razorpay) {
        loadRazorpayScript();
      } else {
        setRazorpayLoaded(true);
      }

      await fetchBillingSummary();
    };

    initializePayment();
  }, []);

  const formatCurrency = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return num.toFixed(2);
  };

  const fetchBillingSummary = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails?.order_id) {
        throw new Error('Order ID not found in booking details');
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/guest/billing-summary/?order_id=${bookingDetails.order_id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch billing summary');
      }

      const data = await response.json();
      setBillingSummary(data);

      // Calculate and set amount due
      const due = data.payment_summary?.total_due || data.total_amounts?.pending || 0;
      setAmountDue(due);
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      toast.error('Error loading billing summary. Please try again.');
    }
  };

  const handleAutoAssignCheckin = async () => {
    try {
      const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
      if (!bookingDetails?.order_id) {
        throw new Error('Order ID not found in booking details');
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/checkin-auto-assign/?order_id=${bookingDetails.order_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message ||
          'Rooms cannot be auto-assigned at this time. This may be due to room unavailability or existing assignments.';
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error('Failed to auto assign rooms');
      }

      const data = await response.json();

      const assignedRoomsData = data.assigned_rooms.map((room) => ({
        booking_id: room.booking_id,
        room_number: room.room_number,
        room_type: room.room_type,
        room_description: room.room_description,
        room_images: room.room_images || [],
        roomtype_images: room.roomtype_images || [],
        max_occupancy: room.max_occupancy,
        base_price: room.base_price,
        amenities: room.amenities || [],
        room_services: room.room_services || [],
      }));

      localStorage.setItem('assignedRooms', JSON.stringify(assignedRoomsData));
      return assignedRoomsData;
    } catch (error) {
      console.error('Auto assign checkin error:', error);
      throw error;
    }
  };

  const handleProceedToNextStep = async () => {
    try {
      setIsProcessing(true);
      await handleAutoAssignCheckin();
      toast.success('Check-in completed successfully!');
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error processing check-in:', error);
      if (
        error.message.includes('cannot be auto-assigned') ||
        error.message.includes('room unavailability') ||
        error.message.includes('existing assignments')
      ) {
        toast.error(
          'Unable to auto-assign rooms. Please contact the front desk manager for assistance with manual room assignment.',
          {
            position: 'top-center',
            autoClose: 8000,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else {
        toast.error('Error processing check-in. Please try again or contact front desk manager.', {
          position: 'top-center',
          autoClose: 5000,
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentOption = async (paymentMethod) => {
    if (!razorpayLoaded && paymentMethod === 'payNow') {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (
      !billingSummary?.payment_summary?.unpaid_billing_ids?.length &&
      paymentMethod !== 'billToCompany' &&
      paymentMethod !== 'payLater'
    ) {
      toast.error('No pending payments found.');
      return;
    }

    if (paymentMethod === 'payNow') {
      try {
        setIsProcessing(true);
        const bookingId = bookingDetails?.primaryBooking?.booking_id;

        if (!bookingId) {
          toast.error('Booking details not found. Please try again.');
          setIsProcessing(false);
          return;
        }

        const unpaidBillingIds = billingSummary.payment_summary.unpaid_billing_ids;
        const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${bookingId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(unpaidBillingIds),
        });

        if (!response.ok) throw new Error('Failed to create payment order');

        const orderData = await response.json();

        if (orderData && orderData.razorpay_order_id) {
          const processingTimeout = setTimeout(() => {
            setIsProcessing(false);
            toast.warn('Payment process timed out. Please try again.');
          }, 5000);

          const options = {
            key: REACT_APP_RAZORPAY_KEY,
            amount: orderData.amount,
            currency: 'INR',
            name: 'The Pagoda Hotel',
            description: `Payment for booking #${bookingDetails.order_id}`,
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              clearTimeout(processingTimeout);
              const paymentDetails = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                billing_ids: unpaidBillingIds,
              };

              try {
                const verificationResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                    },
                    body: JSON.stringify(paymentDetails),
                  }
                );

                if (verificationResponse.ok) {
                  try {
                    await handleAutoAssignCheckin();
                    toast.success('Payment successful and verified!');
                    setCurrentStep(currentStep + 1);
                  } catch (assignError) {
                    toast.error(
                      'Payment successful but room assignment failed. Please contact front desk manager to complete check-in.',
                      {
                        position: 'top-center',
                        autoClose: 8000,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                      }
                    );
                    setIsProcessing(false);
                  }
                } else {
                  toast.error('Payment verification failed.');
                }
              } catch (error) {
                console.error('Verification error:', error);
                toast.error('Error verifying payment. Please try again.');
              } finally {
                setIsProcessing(false);
              }
            },
            prefill: {
              name: `${bookingDetails.primaryBooking?.guest_name || ''}`,
              email: bookingDetails.primaryBooking?.guest_email || '',
              contact: bookingDetails.primaryBooking?.guest_phone?.toString() || '',
            },
            theme: {
              color: '#3399cc',
            },
          };

          const rzp = new window.Razorpay(options);

          rzp.on('modal.close', function () {
            clearTimeout(processingTimeout);
            setIsProcessing(false);
            toast.info('Payment window closed');
          });

          rzp.on('payment.failed', function (response) {
            clearTimeout(processingTimeout);
            toast.error(`Payment failed: ${response.error.description}`);
            setIsProcessing(false);
          });

          rzp.open();
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Error processing payment. Please try again.');
        setIsProcessing(false);
      }
    } else if (paymentMethod === 'payLater') {
      try {
        setIsProcessing(true);
        await handleAutoAssignCheckin();
        toast.info('Payment deferred. Please pay at the front desk.');
        setPaymentStatus('pending');
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('Pay later error:', error);
        if (
          error.message.includes('cannot be auto-assigned') ||
          error.message.includes('room unavailability')
        ) {
          toast.error(
            'Unable to complete check-in. Please contact the front desk manager for assistance.',
            { position: 'top-center', autoClose: 8000 }
          );
        } else {
          toast.error('Error processing check-in. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
    } else if (paymentMethod === 'billToCompany') {
      try {
        setIsProcessing(true);
        await handleAutoAssignCheckin();
        toast.info('Bill to company option selected. Charges will be billed to your company.');
        setPaymentStatus('corporate');
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('Bill to company error:', error);
        if (
          error.message.includes('cannot be auto-assigned') ||
          error.message.includes('room unavailability')
        ) {
          toast.error(
            'Unable to complete check-in. Please contact the front desk manager for assistance.',
            { position: 'top-center', autoClose: 8000 }
          );
        } else {
          toast.error('Error processing check-in. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Room Charges':
      case 'Room Modifications':
        return <FaBed className="text-orange-500" />;
      case 'Amenity Services':
        return <TbHotelService className="text-green-500" />;
      case 'Security Deposit':
        return <FaShieldAlt className="text-blue-500" />;
      case 'Room Services':
        return <MdRoomService className="text-purple-500" />;
      case 'Food':
        return <FaUtensils className="text-red-500" />;
      case 'Checkout Adjustments':
      case 'Other':
        return <TbHotelService className="text-gray-500" />;
      default:
        return <FaBed className="text-orange-500" />;
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const renderCategoryDetails = (items, categoryName = '') => {
    const itemsArray = Array.isArray(items) ? items : [];
    if (itemsArray.length === 0) return null;

    // ✅ FIX: Sum discount directly from item.discount fields (accurate per-item data)
    const categoryDiscount = itemsArray.reduce((sum, item) => sum + (item.discount || 0), 0);

    return (
      <div className="mt-2 pl-4 border-l-2 border-gray-200">
        {itemsArray.map((item, index) => {
          let displayDescription = item.description || 'Item';

          if (categoryName === 'Room Charges') {
            displayDescription = displayDescription.replace('(after recalculation)', '').trim();
            const roomType = billingSummary?.booking?.room_type || 'Double Bed';
            displayDescription = `Room Rent - ${roomType}`;
          }

          return (
            <div key={index} className="py-2 flex justify-between items-center">
              <div className="text-sm text-gray-600 flex-1">{displayDescription}</div>
              <div className="text-sm text-right mr-4">
                <div className="font-medium text-black">
                  ₹{formatCurrency(item.total_amount || 0)}
                </div>
                <div className="text-xs text-gray-500">
                  (₹{formatCurrency(item.amount || 0)} + ₹{formatCurrency(item.tax || 0)} tax)
                </div>
              </div>
            </div>
          );
        })}

        {/* ✅ FIX: Show per-category discount from item.discount sum */}
        {categoryDiscount > 0 && (
          <div className="py-2 flex justify-between items-center text-green-600 border-t border-gray-100 pt-2">
            <div className="flex items-center gap-2">
              <FaTag className="text-xs" />
              <span className="text-sm">Discount Applied</span>
            </div>
            <div className="text-sm mr-3 font-medium">₹{formatCurrency(categoryDiscount)}</div>
          </div>
        )}

        <div className="p-3 mt-3 border-t border-gray-200 flex justify-between font-medium">
          <span>Subtotal</span>
          <span>
            ₹{formatCurrency(itemsArray.reduce((sum, item) => sum + (item.total_amount || 0), 0))}
          </span>
        </div>
      </div>
    );
  };

  const getCategoryKey = (categoryName) => {
    switch (categoryName) {
      case 'Room Charges':
        return 'roomCharges';
      case 'Amenity Services':
        return 'amenities';
      case 'Security Deposit':
        return 'securityDeposit';
      case 'Other':
        return 'otherCharges';
      case 'Food':
        return 'food';
      case 'Room Services':
        return 'roomServices';
      case 'Checkout Adjustments':
        return 'checkoutAdjustments';
      default:
        return categoryName.toLowerCase().replace(/\s+/g, '');
    }
  };

  const getCategoryDisplayName = (categoryName) => {
    const displayNames = {
      'Room Charges': 'Room Charges',
      'Amenity Services': 'Amenities',
      'Security Deposit': 'Security Deposit',
      Other: 'Other Charges',
      Food: 'Food & Beverages',
      'Room Services': 'Room Services',
      'Checkout Adjustments': 'Checkout Adjustments',
    };
    return displayNames[categoryName] || categoryName;
  };

  const getCategoryTotal = (categoryName) => {
    const items = billingSummary?.order_details?.[categoryName] || [];
    return items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  };

  const getCategoryCount = (categoryName) => {
    const items = billingSummary?.order_details?.[categoryName] || [];
    return items.length;
  };

  const getAllRoomCharges = () => {
    return billingSummary?.order_details?.['Room Charges'] || [];
  };

  const getOrderCategories = () => {
    if (!billingSummary?.order_details) return [];

    return Object.entries(billingSummary.order_details)
      .filter(([category, items]) => {
        if (category === 'Room Charges') return false;
        return Array.isArray(items) && items.length > 0;
      })
      .sort(([catA], [catB]) => {
        const order = [
          'Food',
          'Amenity Services',
          'Room Services',
          'Security Deposit',
          'Checkout Adjustments',
          'Other',
        ];
        return order.indexOf(catA) - order.indexOf(catB);
      });
  };

  return (
    <div className="max-w-8xl mx-auto p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Payment</h2>
        <p className="text-gray-600">Verify details and complete your payment</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-12">
        {/* Left Column - Payment Details */}
        <div className="lg:w-3/4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              {billingSummary && (
                <div className="space-y-4">
                  {/* Room Charges */}
                  {(() => {
                    const allRoomCharges = getAllRoomCharges();
                    if (allRoomCharges.length === 0) return null;

                    const categoryKey = getCategoryKey('Room Charges');
                    const total = getCategoryTotal('Room Charges');
                    const count = getCategoryCount('Room Charges');

                    return (
                      <div className="border rounded-lg overflow-hidden">
                        <div
                          className={`p-4 flex justify-between items-center cursor-pointer ${
                            expandedCategories[categoryKey] ? 'bg-blue-50' : 'bg-gray-50'
                          }`}
                          onClick={() => toggleCategory(categoryKey)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              {getCategoryIcon('Room Charges')}
                            </div>
                            <div className="flex items-center">
                              <div>
                                <h4 className="font-medium text-gray-900">Room Charges</h4>
                                <div
                                  className="flex items-center text-xs text-gray-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(categoryKey);
                                  }}
                                >
                                  {count} {count === 1 ? 'item' : 'items'}
                                  {expandedCategories[categoryKey] ? (
                                    <FaChevronUp className="ml-1" />
                                  ) : (
                                    <FaChevronDown className="ml-1" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="font-medium">₹{formatCurrency(total)}</div>
                        </div>
                        {expandedCategories[categoryKey] &&
                          renderCategoryDetails(allRoomCharges, 'Room Charges')}
                      </div>
                    );
                  })()}

                  {/* Other categories */}
                  {getOrderCategories().map(([categoryName, items]) => {
                    const categoryKey = getCategoryKey(categoryName);
                    const displayName = getCategoryDisplayName(categoryName);
                    const total = getCategoryTotal(categoryName);
                    const count = getCategoryCount(categoryName);

                    return (
                      <div key={categoryName} className="border rounded-lg overflow-hidden">
                        <div
                          className={`p-4 flex justify-between items-center cursor-pointer ${
                            expandedCategories[categoryKey] ? 'bg-blue-50' : 'bg-gray-50'
                          }`}
                          onClick={() => toggleCategory(categoryKey)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              {getCategoryIcon(categoryName)}
                            </div>
                            <div className="flex items-center">
                              <div>
                                <h4 className="font-medium text-gray-900">{displayName}</h4>
                                <div
                                  className="flex items-center text-xs text-gray-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(categoryKey);
                                  }}
                                >
                                  {count} {count === 1 ? 'item' : 'items'}
                                  {expandedCategories[categoryKey] ? (
                                    <FaChevronUp className="ml-1" />
                                  ) : (
                                    <FaChevronDown className="ml-1" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="font-medium">₹{formatCurrency(total)}</div>
                        </div>
                        {expandedCategories[categoryKey] &&
                          renderCategoryDetails(items, categoryName)}
                      </div>
                    );
                  })}

                  {/* ───── Summary Section ───── */}
                  <div className="border-t border-gray-200 pt-4 mt-2">
                    {/* ✅ FIX: Total Amount → gross_billed (4685) */}
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Total Amount</span>
                      <span className="font-medium">
                        ₹
                        {formatCurrency(
                          billingSummary.payment_summary?.gross_billed ||
                            billingSummary.total_amounts?.gross ||
                            0
                        )}
                      </span>
                    </div>

                    {/* ✅ FIX: Total Discount → abs(total_credits) = 3000 */}
                    {(() => {
                      const credits = billingSummary.payment_summary?.total_credits || 0;
                      const discountDisplay = Math.abs(credits);
                      if (discountDisplay > 0) {
                        return (
                          <div className="flex justify-between py-2">
                            <span className="text-gray-600">Total Discount</span>
                            <span className="font-medium text-green-600">
                              ₹{formatCurrency(discountDisplay)}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Amount Paid</span>
                      <span className="font-medium">
                        ₹
                        {formatCurrency(
                          billingSummary.payment_summary?.total_paid ||
                            billingSummary.total_amounts?.paid ||
                            0
                        )}
                      </span>
                    </div>

                    {/* Refund — only show if > 0 */}
                    {(() => {
                      const refundAmount =
                        billingSummary.payment_summary?.total_refunded ||
                        billingSummary.total_amounts?.refunded ||
                        0;
                      if (refundAmount > 0) {
                        return (
                          <div className="flex justify-between py-2">
                            <span className="text-gray-600">Refund Amount</span>
                            <span className="font-medium text-green-600">
                              ₹{formatCurrency(refundAmount)}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                      <span className="text-gray-600 font-semibold">Amount Due</span>
                      <span className="font-bold text-red-600">
                        ₹
                        {formatCurrency(
                          billingSummary.payment_summary?.total_due ||
                            billingSummary.total_amounts?.pending ||
                            0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Payment Options */}
        <div className="w-full md:w-2/3 lg:w-1/2 xl:w-1/3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-6">
              {amountDue === 0 ? (
                <div className="text-center py-8">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="text-green-600 text-5xl mb-4">✓</div>
                    <h3 className="text-xl font-semibold text-green-800 mb-2">No Payment Due</h3>
                    <p className="text-green-600">
                      All charges have been paid. You can proceed to check-in.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                    Choose a Payment Option
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                    {/* Pay Now */}
                    <div
                      onClick={() => !isProcessing && handlePaymentOption('payNow')}
                      className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
                        paymentStatus === 'full'
                          ? 'border-blue-500 bg-blue-50'
                          : isProcessing
                            ? 'opacity-50 cursor-not-allowed border-gray-200'
                            : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div
                          className={`p-2 sm:p-3 rounded-full ${
                            paymentStatus === 'full' ? 'bg-blue-100' : 'bg-gray-100'
                          }`}
                        >
                          <FaCreditCard
                            className={`text-lg ${
                              paymentStatus === 'full' ? 'text-blue-600' : 'text-gray-600'
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 truncate">Pay Now</h4>
                          <p className="text-sm text-gray-600 mt-1 truncate">
                            Secure online payment with Razorpay
                          </p>
                        </div>
                        {paymentStatus === 'full' && (
                          <div className="ml-auto bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                            Selected
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pay Later — leisure guests only */}
                    {!isCorporateGuest && (
                      <div
                        onClick={() => !isProcessing && handlePaymentOption('payLater')}
                        className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
                          paymentStatus === 'pending'
                            ? 'border-green-500 bg-green-50'
                            : isProcessing
                              ? 'opacity-50 cursor-not-allowed border-gray-200'
                              : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div
                            className={`p-2 sm:p-3 rounded-full ${
                              paymentStatus === 'pending' ? 'bg-green-100' : 'bg-gray-100'
                            }`}
                          >
                            <FaBed
                              className={`text-lg ${
                                paymentStatus === 'pending' ? 'text-green-600' : 'text-gray-600'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-900 truncate">Pay at Front Desk</h4>
                            <p className="text-sm text-gray-600 mt-1 truncate">
                              Pay when you arrive at the front desk
                            </p>
                          </div>
                          {paymentStatus === 'pending' && (
                            <div className="ml-auto bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                              Selected
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Bill to Company — corporate guests only */}
                    {isCorporateGuest && (
                      <div
                        onClick={() => !isProcessing && handlePaymentOption('billToCompany')}
                        className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
                          paymentStatus === 'corporate'
                            ? 'border-purple-500 bg-purple-50'
                            : isProcessing
                              ? 'opacity-50 cursor-not-allowed border-gray-200'
                              : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div
                            className={`p-2 sm:p-3 rounded-full ${
                              paymentStatus === 'corporate' ? 'bg-purple-100' : 'bg-gray-100'
                            }`}
                          >
                            <FaBuilding
                              className={`text-lg ${
                                paymentStatus === 'corporate' ? 'text-purple-600' : 'text-gray-600'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-900 truncate">Bill to Company</h4>
                            <p className="text-sm text-gray-600 mt-1 truncate">
                              Charges will be billed to {guestData?.company_name}
                            </p>
                          </div>
                          {paymentStatus === 'corporate' && (
                            <div className="ml-auto bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                              Selected
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Next Button — shown only when no amount is due */}
      {amountDue === 0 && (
        <div className="fixed bottom-14 right-12 z-10">
          <button
            onClick={handleProceedToNextStep}
            disabled={isProcessing}
            className={`bg-black hover:text-gray-300 text-white font-semibold py-3 px-28 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 ${
              isProcessing
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-xl transform hover:scale-10'
            }`}
          >
            {isProcessing ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                Next
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Payment;

// import { useEffect, useState } from "react";
// import {
//   FaCreditCard,
//   FaBed,
//   FaShieldAlt,
//   FaChevronDown,
//   FaChevronUp,
//   FaUtensils,
//   FaBuilding,
//   FaTag,
// } from "react-icons/fa";
// import { MdUpgrade, MdRoomService } from "react-icons/md";
// import { TbHotelService } from "react-icons/tb";
// import { toast } from "react-toastify";
// import Cookies from "js-cookie";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// const Payment = ({ currentStep, setCurrentStep, totalSteps }) => {
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [paymentStatus, setPaymentStatus] = useState(null);
//   const [bookingDetails, setBookingDetails] = useState(null);
//   const [billingSummary, setBillingSummary] = useState(null);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [expandedCategories, setExpandedCategories] = useState({
//     roomCharges: false,
//     amenities: false,
//     securityDeposit: false,
//     otherCharges: false,
//     food: false,
//     roomServices: false,
//     checkoutAdjustments: false,
//   });
//   const [guestData, setGuestData] = useState(null);
//   const [isCorporateGuest, setIsCorporateGuest] = useState(false);
//   const [amountDue, setAmountDue] = useState(0);

//   // Function to get guest data
//   const getGuestData = async () => {
//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) return null;

//     try {
//       const guestId =
//         Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//       if (!guestId) return null;

//       const profileResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();

//       // Check if this is a corporate guest
//       const isCorporateGuest = profileData.companyid && profileData.companyname;

//       const isDecrypted =
//         profileData.emailid && profileData.emailid.length < 50;

//       if (isDecrypted) {
//         return {
//           first_name: profileData.firstname || "",
//           last_name: profileData.lastname || "",
//           email: profileData.emailid || "",
//           phone_number: profileData.phonenumber || "",
//           country_code: "+91",
//           client_type: profileData.clienttype || "Leisure",
//           company_id: profileData.companyid || "",
//           company_name: profileData.companyname || "",
//           is_corporate: isCorporateGuest,
//         };
//       }

//       const decryptedFields = await decryptUserData({
//         email: profileData.emailid,
//         phone_number: profileData.phonenumber,
//         country_code: profileData.countrycode,
//       });

//       return {
//         first_name: profileData.firstname || "",
//         last_name: profileData.lastname || "",
//         email: decryptedFields.email || profileData.emailid || "",
//         phone_number:
//           decryptedFields.phone_number || profileData.phonenumber || "",
//         country_code: "+91",
//         client_type: profileData.clienttype || "Leisure",
//         company_id: profileData.companyid || "",
//         company_name: profileData.companyname || "",
//         is_corporate: isCorporateGuest,
//       };
//     } catch (error) {
//       console.error("Error fetching guest data:", error);
//       return null;
//     }
//   };

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   const decryptUserData = async (encryptedData) => {
//     try {
//       if (encryptedData.email && encryptedData.email.length < 50) {
//         return encryptedData;
//       }

//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: {
//             email: encryptedData.email,
//             phone_number: encryptedData.phone_number,
//             country_code: encryptedData.country_code,
//           },
//         }),
//       });

//       if (response.status === 401) {
//         return encryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error("Decryption error:", error);
//       return encryptedData;
//     }
//   };

//   useEffect(() => {
//     const initializePayment = async () => {
//       const bookingDetails = JSON.parse(localStorage.getItem("bookingDetails"));
//       setBookingDetails(bookingDetails);

//       // Load guest data and check if corporate
//       const guestInfo = await getGuestData();
//       setGuestData(guestInfo);
//       setIsCorporateGuest(guestInfo?.is_corporate || false);

//       const loadRazorpayScript = () => {
//         const script = document.createElement("script");
//         script.src = "https://checkout.razorpay.com/v1/checkout.js";
//         script.async = true;
//         script.onload = () => setRazorpayLoaded(true);
//         script.onerror = () => {
//           toast.error("Error loading Razorpay script. Please try again.");
//         };
//         document.body.appendChild(script);
//       };

//       if (!window.Razorpay) {
//         loadRazorpayScript();
//       } else {
//         setRazorpayLoaded(true);
//       }

//       await fetchBillingSummary();
//     };

//     initializePayment();
//   }, []);

//   const formatCurrency = (amount) => {
//     const num = typeof amount === "string" ? parseFloat(amount) : amount;
//     return num.toFixed(2);
//   };

//   const fetchBillingSummary = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem("bookingDetails"));
//       if (!bookingDetails?.order_id) {
//         throw new Error("Order ID not found in booking details");
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/guest/billing-summary/?order_id=${bookingDetails.order_id}`,
//         {
//           method: "GET",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Failed to fetch billing summary");
//       }

//       const data = await response.json();
//       setBillingSummary(data);

//       // Calculate and set amount due
//       const due = data.payment_summary?.total_due ||
//                   data.total_amounts?.pending ||
//                   0;
//       setAmountDue(due);
//     } catch (error) {
//       console.error("Error fetching billing summary:", error);
//       toast.error("Error loading billing summary. Please try again.");
//     }
//   };

//   const handleAutoAssignCheckin = async () => {
//     try {
//       const bookingDetails = JSON.parse(localStorage.getItem("bookingDetails"));
//       if (!bookingDetails?.order_id) {
//         throw new Error("Order ID not found in booking details");
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/checkin-auto-assign/?order_id=${bookingDetails.order_id}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//         }
//       );

//       // Handle 409 Conflict specifically
//       if (response.status === 409) {
//         const errorData = await response.json().catch(() => ({}));
//         const errorMessage = errorData.message || "Rooms cannot be auto-assigned at this time. This may be due to room unavailability or existing assignments.";

//         throw new Error(errorMessage);
//       }

//       if (!response.ok) {
//         throw new Error("Failed to auto assign rooms");
//       }

//       const data = await response.json();

//       const assignedRoomsData = data.assigned_rooms.map((room) => ({
//         booking_id: room.booking_id,
//         room_number: room.room_number,
//         room_type: room.room_type,
//         room_description: room.room_description,
//         room_images: room.room_images || [],
//         roomtype_images: room.roomtype_images || [],
//         max_occupancy: room.max_occupancy,
//         base_price: room.base_price,
//         amenities: room.amenities || [],
//         room_services: room.room_services || [],
//       }));

//       localStorage.setItem("assignedRooms", JSON.stringify(assignedRoomsData));
//       return assignedRoomsData;
//     } catch (error) {
//       console.error("Auto assign checkin error:", error);
//       throw error;
//     }
//   };

//   const handleProceedToNextStep = async () => {
//     try {
//       setIsProcessing(true);
//       await handleAutoAssignCheckin();
//       toast.success("Check-in completed successfully!");
//       setCurrentStep(currentStep + 1);
//     } catch (error) {
//       console.error("Error processing check-in:", error);

//       // Check if it's a conflict error (409)
//       if (error.message.includes("cannot be auto-assigned") ||
//           error.message.includes("room unavailability") ||
//           error.message.includes("existing assignments")) {
//         toast.error(
//           "Unable to auto-assign rooms. Please contact the front desk manager for assistance with manual room assignment.",
//           {
//             position: "top-center",
//             autoClose: 8000,
//             closeOnClick: true,
//             pauseOnHover: true,
//             draggable: true,
//           }
//         );
//       } else {
//         toast.error("Error processing check-in. Please try again or contact front desk manager.", {
//           position: "top-center",
//           autoClose: 5000,
//         });
//       }
//     } finally {
//       setIsProcessing(false);
//     }
//   };

//   const handlePaymentOption = async (paymentMethod) => {
//     if (!razorpayLoaded && paymentMethod === "payNow") {
//       toast.error("Razorpay script is not loaded yet.");
//       return;
//     }

//     if (!billingSummary?.payment_summary?.unpaid_billing_ids?.length &&
//         paymentMethod !== "billToCompany" &&
//         paymentMethod !== "payLater") {
//       toast.error("No pending payments found.");
//       return;
//     }

//     if (paymentMethod === "payNow") {
//       try {
//         setIsProcessing(true);
//         const bookingId = bookingDetails?.primaryBooking?.booking_id;

//         if (!bookingId) {
//           toast.error("Booking details not found. Please try again.");
//           setIsProcessing(false);
//           return;
//         }

//         const unpaidBillingIds =
//           billingSummary.payment_summary.unpaid_billing_ids;
//         const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${bookingId}`;

//         const response = await fetch(apiUrl, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//           body: JSON.stringify(unpaidBillingIds),
//         });

//         if (!response.ok) throw new Error("Failed to create payment order");

//         const orderData = await response.json();

//         if (orderData && orderData.razorpay_order_id) {
//           const processingTimeout = setTimeout(() => {
//             setIsProcessing(false);
//             toast.warn("Payment process timed out. Please try again.");
//           }, 5000);

//           const options = {
//             key: REACT_APP_RAZORPAY_KEY,
//             amount: orderData.amount,
//             currency: "INR",
//             name: "The Pagoda Hotel",
//             description: `Payment for booking #${bookingDetails.order_id}`,
//             order_id: orderData.razorpay_order_id,
//             handler: async function (response) {
//               clearTimeout(processingTimeout);
//               const paymentDetails = {
//                 razorpay_order_id: response.razorpay_order_id,
//                 razorpay_payment_id: response.razorpay_payment_id,
//                 razorpay_signature: response.razorpay_signature,
//                 billing_ids: unpaidBillingIds,
//               };

//               try {
//                 const verificationResponse = await fetch(
//                   `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
//                   {
//                     method: "POST",
//                     headers: {
//                       "Content-Type": "application/json",
//                       Accept: "application/json",
//                     },
//                     body: JSON.stringify(paymentDetails),
//                   }
//                 );

//                 if (verificationResponse.ok) {
//                   try {
//                     await handleAutoAssignCheckin();
//                     toast.success("Payment successful and verified!");
//                     setCurrentStep(currentStep + 1);
//                   } catch (assignError) {
//                     // Handle room assignment error after successful payment
//                     toast.error(
//                       "Payment successful but room assignment failed. Please contact front desk manager to complete check-in.",
//                       {
//                         position: "top-center",
//                         autoClose: 8000,
//                         closeOnClick: true,
//                         pauseOnHover: true,
//                         draggable: true,
//                       }
//                     );
//                     // Still proceed to next step? Or stay on payment step?
//                     // setCurrentStep(currentStep + 1); // Uncomment if you still want to proceed
//                     setIsProcessing(false);
//                   }
//                 } else {
//                   toast.error("Payment verification failed.");
//                 }
//               } catch (error) {
//                 console.error("Verification error:", error);
//                 toast.error("Error verifying payment. Please try again.");
//               } finally {
//                 setIsProcessing(false);
//               }
//             },
//             prefill: {
//               name: `${bookingDetails.primaryBooking?.guest_name || ""}`,
//               email: bookingDetails.primaryBooking?.guest_email || "",
//               contact:
//                 bookingDetails.primaryBooking?.guest_phone?.toString() || "",
//             },
//             theme: {
//               color: "#3399cc",
//             },
//           };

//           const rzp = new window.Razorpay(options);

//           rzp.on("modal.close", function () {
//             clearTimeout(processingTimeout);
//             setIsProcessing(false);
//             toast.info("Payment window closed");
//           });

//           rzp.on("payment.failed", function (response) {
//             clearTimeout(processingTimeout);
//             toast.error(`Payment failed: ${response.error.description}`);
//             setIsProcessing(false);
//           });

//           rzp.open();
//         }
//       } catch (error) {
//         console.error("Payment error:", error);
//         toast.error("Error processing payment. Please try again.");
//         setIsProcessing(false);
//       }
//     } else if (paymentMethod === "payLater") {
//       try {
//         setIsProcessing(true);
//         await handleAutoAssignCheckin();
//         toast.info("Payment deferred. Please pay at the front desk.");
//         setPaymentStatus("pending");
//         setCurrentStep(currentStep + 1);
//       } catch (error) {
//         console.error("Pay later error:", error);
//         if (error.message.includes("cannot be auto-assigned") ||
//             error.message.includes("room unavailability")) {
//           toast.error(
//             "Unable to complete check-in. Please contact the front desk manager for assistance.",
//             {
//               position: "top-center",
//               autoClose: 8000,
//             }
//           );
//         } else {
//           toast.error("Error processing check-in. Please try again.");
//         }
//       } finally {
//         setIsProcessing(false);
//       }
//     } else if (paymentMethod === "billToCompany") {
//       try {
//         setIsProcessing(true);
//         await handleAutoAssignCheckin();
//         toast.info(
//           "Bill to company option selected. Charges will be billed to your company."
//         );
//         setPaymentStatus("corporate");
//         setCurrentStep(currentStep + 1);
//       } catch (error) {
//         console.error("Bill to company error:", error);
//         if (error.message.includes("cannot be auto-assigned") ||
//             error.message.includes("room unavailability")) {
//           toast.error(
//             "Unable to complete check-in. Please contact the front desk manager for assistance ",
//             {
//               position: "top-center",
//               autoClose: 8000,
//             }
//           );
//         } else {
//           toast.error("Error processing check-in. Please try again.");
//         }
//       } finally {
//         setIsProcessing(false);
//       }
//     }
//   };

//   const getCategoryIcon = (category) => {
//     switch (category) {
//       case "Room Charges":
//       case "Room Modifications":
//         return <FaBed className="text-orange-500" />;
//       case "Amenity Services":
//         return <TbHotelService className="text-green-500" />;
//       case "Security Deposit":
//         return <FaShieldAlt className="text-blue-500" />;
//       case "Room Services":
//         return <MdRoomService className="text-purple-500" />;
//       case "Food":
//         return <FaUtensils className="text-red-500" />;
//       case "Checkout Adjustments":
//       case "Other":
//         return <TbHotelService className="text-gray-500" />;
//       default:
//         return <FaBed className="text-orange-500" />;
//     }
//   };

//   const toggleCategory = (category) => {
//     setExpandedCategories((prev) => ({
//       ...prev,
//       [category]: !prev[category],
//     }));
//   };

//   const renderCategoryDetails = (items, categoryName = "") => {
//     const itemsArray = Array.isArray(items) ? items : [];
//     if (itemsArray.length === 0) return null;

//     // Get discount for this category from billing_summary
//     const getCategoryDiscount = () => {
//       if (!billingSummary?.billing_summary?.by_type) return 0;
//       const categoryData = billingSummary.billing_summary.by_type.find(
//         type => type.billing_type === categoryName
//       );
//       return categoryData?.total_discount || 0;
//     };

//     const categoryDiscount = getCategoryDiscount();

//     return (
//       <div className="mt-2 pl-4 border-l-2 border-gray-200">
//         {itemsArray.map((item, index) => {
//           // Format description for Room Charges
//           let displayDescription = item.description || "Item";

//           if (categoryName === "Room Charges") {
//             // Remove "(after recalculation)" from description
//             displayDescription = displayDescription.replace("(after recalculation)", "").trim();

//             // Get room type from booking details
//             const roomType = billingSummary?.booking?.room_type || "Double Bed";

//             // Format as "Room Rent - Double Bed"
//             displayDescription = `Room Rent - ${roomType}`;
//           }

//           return (
//             <div key={index} className="py-2 flex justify-between items-center">
//               <div className="text-sm text-gray-600 flex-1">
//                 {displayDescription}
//               </div>
//               <div className="text-sm text-right mr-4">
//                 <div className={`font-medium text-black`}>
//                   ₹{formatCurrency(item.total_amount || 0)}
//                 </div>
//                 <div className="text-xs text-gray-500">
//                 (₹{formatCurrency(item.amount || 0)} + ₹
//                 {formatCurrency(item.tax || 0)} tax)
//               </div>
//               </div>
//             </div>
//           );
//         })}

//         {/* Show discount if applicable */}
//         {categoryDiscount > 0 && (
//           <div className="py-2 flex justify-between items-center text-green-600 border-t border-gray-100 pt-2">
//             <div className="flex items-center gap-2">
//               <FaTag className="text-xs" />
//               <span className="text-sm">Discount Applied</span>
//             </div>
//             <div className="text-sm mr-3 font-medium">
//                ₹{formatCurrency(categoryDiscount)}
//             </div>
//           </div>
//         )}

//         <div className="p-3 mt-3 border-t border-gray-200 flex justify-between font-medium">
//           <span>Subtotal</span>
//           <span>
//             ₹
//             {formatCurrency(
//               itemsArray.reduce(
//                 (sum, item) => sum + (item.total_amount || 0),
//                 0
//               )
//             )}
//           </span>
//         </div>
//       </div>
//     );
//   };

//   const getCategoryKey = (categoryName) => {
//     switch (categoryName) {
//       case "Room Charges":
//         return "roomCharges";
//       case "Amenity Services":
//         return "amenities";
//       case "Security Deposit":
//         return "securityDeposit";
//       case "Other":
//         return "otherCharges";
//       case "Food":
//         return "food";
//       case "Room Services":
//         return "roomServices";
//       case "Checkout Adjustments":
//         return "checkoutAdjustments";
//       default:
//         return categoryName.toLowerCase().replace(/\s+/g, "");
//     }
//   };

//   const getCategoryDisplayName = (categoryName) => {
//     const displayNames = {
//       "Room Charges": "Room Charges",
//       "Amenity Services": "Amenities",
//       "Security Deposit": "Security Deposit",
//       Other: "Other Charges",
//       Food: "Food & Beverages",
//       "Room Services": "Room Services",
//       "Checkout Adjustments": "Checkout Adjustments",
//     };
//     return displayNames[categoryName] || categoryName;
//   };

//   const getCategoryTotal = (categoryName) => {
//     const items = billingSummary?.order_details?.[categoryName] || [];
//     return items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
//   };

//   const getCategoryCount = (categoryName) => {
//     const items = billingSummary?.order_details?.[categoryName] || [];
//     return items.length;
//   };

//   // Get all room charges from order_details (recalculated amounts)
//   const getAllRoomCharges = () => {
//     // Get room charges directly from order_details
//     const roomCharges = billingSummary?.order_details?.["Room Charges"] || [];

//     return roomCharges;
//   };

//   // Get all other order categories (excluding Room Charges)
//   const getOrderCategories = () => {
//     if (!billingSummary?.order_details) return [];

//     return Object.entries(billingSummary.order_details)
//       .filter(([category, items]) => {
//         // Exclude Room Charges since we handle it separately
//         if (category === "Room Charges") return false;
//         return Array.isArray(items) && items.length > 0;
//       })
//       .sort(([catA], [catB]) => {
//         const order = [
//           "Food",
//           "Amenity Services",
//           "Room Services",
//           "Security Deposit",
//           "Checkout Adjustments",
//           "Other",
//         ];
//         return order.indexOf(catA) - order.indexOf(catB);
//       });
//   };

//   return (
//     <div className="max-w-8xl mx-auto p-4">
//       <div className="mb-4">
//         <h2 className="text-2xl font-bold text-gray-900">Payment</h2>
//         <p className="text-gray-600">
//           Verify details and complete your payment
//         </p>
//       </div>

//       <div className="flex flex-col lg:flex-row gap-6 mb-12">
//         {/* Left Column - Payment Details */}
//         <div className="lg:w-3/4">
//           <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
//             <div className="p-6">
//               {billingSummary && (
//                 <div className="space-y-4">
//                   {/* Room Charges (from order_details) */}
//                   {(() => {
//                     const allRoomCharges = getAllRoomCharges();
//                     if (allRoomCharges.length === 0) return null;

//                     const categoryKey = getCategoryKey("Room Charges");
//                     const total = getCategoryTotal("Room Charges");
//                     const count = getCategoryCount("Room Charges");

//                     return (
//                       <div className="border rounded-lg overflow-hidden">
//                         <div
//                           className={`p-4 flex justify-between items-center cursor-pointer ${
//                             expandedCategories[categoryKey]
//                               ? "bg-blue-50"
//                               : "bg-gray-50"
//                           }`}
//                           onClick={() => toggleCategory(categoryKey)}
//                         >
//                           <div className="flex items-center gap-3">
//                             <div className="p-2 rounded-lg bg-blue-100">
//                               {getCategoryIcon("Room Charges")}
//                             </div>
//                             <div className="flex items-center">
//                               <div>
//                                 <h4 className="font-medium text-gray-900">
//                                   Room Charges
//                                 </h4>
//                                 <div
//                                   className="flex items-center text-xs text-gray-500"
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     toggleCategory(categoryKey);
//                                   }}
//                                 >
//                                   {count} {count === 1 ? "item" : "items"}
//                                   {expandedCategories[categoryKey] ? (
//                                     <FaChevronUp className="ml-1" />
//                                   ) : (
//                                     <FaChevronDown className="ml-1" />
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           </div>
//                           <div className="font-medium">
//                             ₹{formatCurrency(total)}
//                           </div>
//                         </div>
//                         {expandedCategories[categoryKey] &&
//                           renderCategoryDetails(allRoomCharges, "Room Charges")}
//                       </div>
//                     );
//                   })()}

//                   {/* Other categories from order_details */}
//                   {getOrderCategories().map(([categoryName, items]) => {
//                     const categoryKey = getCategoryKey(categoryName);
//                     const displayName = getCategoryDisplayName(categoryName);
//                     const total = getCategoryTotal(categoryName);
//                     const count = getCategoryCount(categoryName);

//                     return (
//                       <div
//                         key={categoryName}
//                         className="border rounded-lg overflow-hidden"
//                       >
//                         <div
//                           className={`p-4 flex justify-between items-center cursor-pointer ${
//                             expandedCategories[categoryKey]
//                               ? "bg-blue-50"
//                               : "bg-gray-50"
//                           }`}
//                           onClick={() => toggleCategory(categoryKey)}
//                         >
//                           <div className="flex items-center gap-3">
//                             <div className="p-2 rounded-lg bg-blue-100">
//                               {getCategoryIcon(categoryName)}
//                             </div>
//                             <div className="flex items-center">
//                               <div>
//                                 <h4 className="font-medium text-gray-900">
//                                   {displayName}
//                                 </h4>
//                                 <div
//                                   className="flex items-center text-xs text-gray-500"
//                                   onClick={(e) => {
//                                     e.stopPropagation();
//                                     toggleCategory(categoryKey);
//                                   }}
//                                 >
//                                   {count} {count === 1 ? "item" : "items"}
//                                   {expandedCategories[categoryKey] ? (
//                                     <FaChevronUp className="ml-1" />
//                                   ) : (
//                                     <FaChevronDown className="ml-1" />
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           </div>
//                           <div className="font-medium">
//                             ₹{formatCurrency(total)}
//                           </div>
//                         </div>
//                         {expandedCategories[categoryKey] &&
//                           renderCategoryDetails(items, categoryName)}
//                       </div>
//                     );
//                   })}

//                   {/* Summary */}
//                   {/* Summary */}
// <div className="border-t border-gray-200 pt-4 mt-2">
//   <div className="flex justify-between py-2">
//     <span className="text-gray-600">Total Amount</span>
//     <span className="font-medium">
//       ₹
//       {formatCurrency(
//         billingSummary.payment_summary?.net_billed ||
//           billingSummary.total_amounts?.final ||
//           0
//       )}
//     </span>
//   </div>

//   {/* Show Discount in Summary */}
//   {billingSummary.total_amounts?.total_discount > 0 && (
//     <div className="flex justify-between py-2">
//       <span className="text-gray-600">Total Discount</span>
//       <span className="font-medium text-green-600">
//         ₹{formatCurrency(billingSummary.total_amounts.total_discount)}
//       </span>
//     </div>
//   )}

//   <div className="flex justify-between py-2">
//     <span className="text-gray-600">Amount Paid</span>
//     <span className="font-medium">
//       ₹
//       {formatCurrency(
//         billingSummary.payment_summary?.total_paid ||
//           billingSummary.total_amounts?.paid ||
//           0
//       )}
//     </span>
//   </div>

//   {/* <div className="flex justify-between py-2">
//     <span className="text-gray-600">Total Tax</span>
//     <span className="font-medium">
//       ₹
//       {formatCurrency(
//         billingSummary.payment_summary?.total_tax ||
//           billingSummary.total_amounts?.tax ||
//           0
//       )}
//     </span>
//   </div> */}

//   {/* Refund Amount - Only show if greater than 0 */}
//   {(() => {
//     const refundAmount = billingSummary.payment_summary?.total_refunded ||
//                          billingSummary.total_amounts?.refunded ||
//                          0;
//     if (refundAmount > 0) {
//       return (
//         <div className="flex justify-between py-2">
//           <span className="text-gray-600">Refund Amount</span>
//           <span className="font-medium text-green-600">
//             ₹{formatCurrency(refundAmount)}
//           </span>
//         </div>
//       );
//     }
//     return null;
//   })()}

//   <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
//     <span className="text-gray-600 font-semibold">Amount Due</span>
//     <span className="font-bold text-red-600">
//       ₹
//       {formatCurrency(
//         billingSummary.payment_summary?.total_due ||
//           billingSummary.total_amounts?.pending ||
//           0
//       )}
//     </span>
//   </div>
// </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Right Column - Payment Options */}
//         <div className="w-full md:w-2/3 lg:w-1/2 xl:w-1/3">
//           <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
//             <div className="p-4 sm:p-6">
//               {amountDue === 0 ? (
//                 // Show message when no payment is due
//                 <div className="text-center py-8">
//                   <div className="bg-green-50 border border-green-200 rounded-xl p-6">
//                     <div className="text-green-600 text-5xl mb-4">✓</div>
//                     <h3 className="text-xl font-semibold text-green-800 mb-2">
//                       No Payment Due
//                     </h3>
//                     <p className="text-green-600">
//                       All charges have been paid. You can proceed to check-in.
//                     </p>
//                   </div>
//                 </div>
//               ) : (
//                 // Show payment options when amount is due
//                 <>
//                   <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
//                     Choose a Payment Option
//                   </h3>
//                   <div className="space-y-3 sm:space-y-4">
//                     {/* Pay Now Option - Available for all users */}
//                     <div
//                       onClick={() => !isProcessing && handlePaymentOption("payNow")}
//                       className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
//                         paymentStatus === "full"
//                           ? "border-blue-500 bg-blue-50"
//                           : isProcessing
//                           ? "opacity-50 cursor-not-allowed border-gray-200"
//                           : "border-gray-200 hover:border-blue-300"
//                       }`}
//                     >
//                       <div className="flex items-center gap-3 sm:gap-4">
//                         <div
//                           className={`p-2 sm:p-3 rounded-full ${
//                             paymentStatus === "full" ? "bg-blue-100" : "bg-gray-100"
//                           }`}
//                         >
//                           <FaCreditCard
//                             className={`text-lg ${
//                               paymentStatus === "full"
//                                 ? "text-blue-600"
//                                 : "text-gray-600"
//                             }`}
//                           />
//                         </div>
//                         <div className="flex-1 min-w-0">
//                           <h4 className="font-bold text-gray-900 truncate">
//                             Pay Now
//                           </h4>
//                           <p className="text-sm text-gray-600 mt-1 truncate">
//                             Secure online payment with Razorpay
//                           </p>
//                         </div>
//                         {paymentStatus === "full" && (
//                           <div className="ml-auto bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
//                             Selected
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     {/* Pay Later Option - Only for leisure guests */}
//                     {!isCorporateGuest && (
//                       <div
//                         onClick={() =>
//                           !isProcessing && handlePaymentOption("payLater")
//                         }
//                         className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
//                           paymentStatus === "pending"
//                             ? "border-green-500 bg-green-50"
//                             : isProcessing
//                             ? "opacity-50 cursor-not-allowed border-gray-200"
//                             : "border-gray-200 hover:border-green-300"
//                         }`}
//                       >
//                         <div className="flex items-center gap-3 sm:gap-4">
//                           <div
//                             className={`p-2 sm:p-3 rounded-full ${
//                               paymentStatus === "pending"
//                                 ? "bg-green-100"
//                                 : "bg-gray-100"
//                             }`}
//                           >
//                             <FaBed
//                               className={`text-lg ${
//                                 paymentStatus === "pending"
//                                   ? "text-green-600"
//                                   : "text-gray-600"
//                               }`}
//                             />
//                           </div>
//                           <div className="flex-1 min-w-0">
//                             <h4 className="font-bold text-gray-900 truncate">
//                               Pay at Front desk
//                             </h4>
//                             <p className="text-sm text-gray-600 mt-1 truncate">
//                               Pay when you arrive at the front desk
//                             </p>
//                           </div>
//                           {paymentStatus === "pending" && (
//                             <div className="ml-auto bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
//                               Selected
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}

//                     {/* Bill to Company Option - Only for corporate guests */}
//                     {isCorporateGuest && (
//                       <div
//                         onClick={() =>
//                           !isProcessing && handlePaymentOption("billToCompany")
//                         }
//                         className={`border-2 rounded-xl p-4 sm:p-5 cursor-pointer transition-all ${
//                           paymentStatus === "corporate"
//                             ? "border-purple-500 bg-purple-50"
//                             : isProcessing
//                             ? "opacity-50 cursor-not-allowed border-gray-200"
//                             : "border-gray-200 hover:border-purple-300"
//                         }`}
//                       >
//                         <div className="flex items-center gap-3 sm:gap-4">
//                           <div
//                             className={`p-2 sm:p-3 rounded-full ${
//                               paymentStatus === "corporate"
//                                 ? "bg-purple-100"
//                                 : "bg-gray-100"
//                             }`}
//                           >
//                             <FaBuilding
//                               className={`text-lg ${
//                                 paymentStatus === "corporate"
//                                   ? "text-purple-600"
//                                   : "text-gray-600"
//                               }`}
//                             />
//                           </div>
//                           <div className="flex-1 min-w-0">
//                             <h4 className="font-bold text-gray-900 truncate">
//                               Bill to Company
//                             </h4>
//                             <p className="text-sm text-gray-600 mt-1 truncate">
//                               Charges will be billed to {guestData?.company_name}
//                             </p>
//                           </div>
//                           {paymentStatus === "corporate" && (
//                             <div className="ml-auto bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
//                               Selected
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Next Button - Bottom Right Corner when amount due is 0 */}
//       {amountDue === 0 && (
//         <div className="fixed bottom-14  right-12 z-10">
//           <button
//             onClick={handleProceedToNextStep}
//             disabled={isProcessing}
//             className={`bg-black hover:text-gray-300 text-white font-semibold py-3 px-28 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 ${
//               isProcessing ? "opacity-50 cursor-not-allowed" : "hover:shadow-xl transform hover:scale-10"
//             }`}
//           >
//             {isProcessing ? (
//               <>
//                 <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                 </svg>
//                 Processing...
//               </>
//             ) : (
//               <>
//                 Next
//                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </>
//             )}
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Payment;
